import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getSubChapterQuestions, startProgress,
  submitMCQ, submitWritten, submitSpoken, completeProgress,
  explainConcept,
} from '../../services/userApi';

// ── Helpers ────────────────────────────────────────────────────────────────────
const TYPE_COLOR = { mcq: '#1D4ED8', written: '#7C3AED', spoken: '#D97706' };
const TYPE_BG    = { mcq: '#DBEAFE', written: '#EDE9FE', spoken: '#FEF3C7' };
const TYPE_LABEL = { mcq: 'Multiple choice', written: 'Written', spoken: 'Spoken' };

function ScoreBadge({ score, max = 10 }) {
  const pct = score / max;
  const color = pct >= 0.7 ? '#16A34A' : pct >= 0.4 ? '#1D4ED8' : '#C1272D';
  const bg    = pct >= 0.7 ? '#DCFCE7' : pct >= 0.4 ? '#DBEAFE' : '#FCEBEB';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color, fontWeight: 700, fontSize: 13, padding: '3px 11px', borderRadius: 20 }}>
      {score}/{max}
    </span>
  );
}

// ── AI avatar ─────────────────────────────────────────────────────────────────
function AIAvatar() {
  return (
    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#1B3A5C,#2A5280)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    </div>
  );
}

// ── MCQ inline component ───────────────────────────────────────────────────────
function MCQOptions({ question, onSubmit, submitted, result }) {
  const [selected, setSelected] = useState(null);
  const LETTERS = ['A','B','C','D'];

  useEffect(() => {
    if (submitted) return;
    const handler = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      const idx = ['1','2','3','4'].indexOf(e.key);
      if (idx !== -1 && question.options?.[idx]) { setSelected(question.options[idx]); return; }
      if (e.key === 'Enter' && selected) { onSubmit(selected); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [submitted, selected, question.options, onSubmit]);

  return (
    <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
      {question.options?.map((opt, i) => {
        const isCorrect  = submitted && opt === result?.correct_answer;
        const isWrong    = submitted && opt === selected && !result?.is_correct;
        const isSelected = !submitted && selected === opt;

        let bg = '#FAFAF8', borderColor = '#E5E2D9', textColor = '#44403C';
        if (isCorrect)  { bg = '#DCFCE7'; borderColor = '#16A34A'; textColor = '#16A34A'; }
        else if (isWrong)    { bg = '#FCEBEB'; borderColor = '#C1272D'; textColor = '#C1272D'; }
        else if (isSelected) { bg = '#DBEAFE'; borderColor = '#1D4ED8'; textColor = '#1D4ED8'; }

        return (
          <button key={i} onClick={() => { if (!submitted) setSelected(opt); }}
            disabled={submitted}
            style={{
              padding: '13px 16px', borderRadius: 12, border: `1.5px solid ${borderColor}`,
              textAlign: 'left', fontSize: 14, lineHeight: 1.5,
              cursor: submitted ? 'default' : 'pointer', transition: 'all 0.12s',
              background: bg, color: textColor,
              fontWeight: isSelected || isCorrect ? 600 : 400,
              display: 'flex', alignItems: 'center', gap: 12,
            }}
            onMouseEnter={e => { if (!submitted && !isSelected) e.currentTarget.style.background='#F2F1ED'; }}
            onMouseLeave={e => { if (!submitted && !isSelected) e.currentTarget.style.background='#FAFAF8'; }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, flexShrink: 0,
              background: isCorrect ? '#16A34A' : isWrong ? '#C1272D' : isSelected ? '#1D4ED8' : '#E5E2D9',
              color: (isCorrect || isWrong || isSelected) ? '#fff' : '#78756E',
            }}>
              {LETTERS[i]}
            </span>
            {opt}
          </button>
        );
      })}
      {!submitted && (
        <>
          <button onClick={() => { if (!selected) return toast.error('Pick an answer'); onSubmit(selected); }}
            disabled={!selected}
            style={{
              marginTop: 4, padding: '11px 24px', borderRadius: 10, border: 'none',
              background: selected ? '#1B3A5C' : '#E5E2D9',
              color: selected ? '#fff' : '#A8A59F',
              fontSize: 14, fontWeight: 700,
              cursor: selected ? 'pointer' : 'not-allowed',
              alignSelf: 'flex-start', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (selected) e.currentTarget.style.background='#142d47'; }}
            onMouseLeave={e => { if (selected) e.currentTarget.style.background='#1B3A5C'; }}>
            Submit answer
          </button>
          <p style={{ fontSize: 11, color: '#9CA3AF', margin: '4px 0 0' }}>Tip: press 1–4 to select, Enter to submit</p>
        </>
      )}
      {submitted && (
        <div style={{ padding: '11px 16px', borderRadius: 10, background: result?.is_correct ? '#DCFCE7' : '#FCEBEB', borderLeft: `3px solid ${result?.is_correct ? '#16A34A' : '#C1272D'}`, fontSize: 13 }}>
          <span style={{ fontWeight: 700, color: result?.is_correct ? '#16A34A' : '#C1272D' }}>
            {result?.is_correct ? 'Correct!' : 'Incorrect'}
          </span>
          {!result?.is_correct && (
            <span style={{ color: '#44403C', marginLeft: 8 }}>
              Correct answer: <strong>{result?.correct_answer}</strong>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Spoken recorder ────────────────────────────────────────────────────────────
function SpokenRecorder({ onReady, loading }) {
  const [recording, setRecording] = useState(false);
  const [audioUrl,  setAudioUrl]  = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration,  setDuration]  = useState(0);
  const mediaRef  = useRef(null);
  const timerRef  = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start(); setRecording(true); setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch { toast.error('Microphone access denied'); }
  };

  const stop = () => { mediaRef.current?.stop(); setRecording(false); clearInterval(timerRef.current); };
  const fmt = s => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  const handleSubmit = () => {
    if (!audioBlob) return;
    const reader = new FileReader();
    reader.onloadend = () => onReady(reader.result.split(',')[1], audioBlob.type);
    reader.readAsDataURL(audioBlob);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      {!recording && !audioUrl && (
        <button onClick={start} aria-label="Start recording"
          style={{ width: 46, height: 46, borderRadius: '50%', background: '#C1272D', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background='#a01f24'}
          onMouseLeave={e => e.currentTarget.style.background='#C1272D'}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="11" rx="3"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        </button>
      )}
      {!recording && !audioUrl && (
        <span style={{ fontSize: 13, color: '#78756E' }}>Click to start recording your spoken answer</span>
      )}
      {recording && (
        <>
          <button onClick={stop}
            style={{ width: 46, height: 46, borderRadius: '50%', background: '#C1272D', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, animation: 'recPulse 1.2s infinite' }}>
            <div style={{ width: 14, height: 14, background: '#fff', borderRadius: 2 }} />
          </button>
          <span style={{ color: '#C1272D', fontWeight: 700, fontSize: 15 }}>{fmt(duration)}</span>
          <span style={{ color: '#78756E', fontSize: 13 }}>Recording… tap to stop</span>
        </>
      )}
      {audioUrl && (
        <>
          <audio controls src={audioUrl} style={{ height: 38, flex: 1, minWidth: 120 }} />
          <button onClick={() => { setAudioBlob(null); setAudioUrl(null); }}
            style={{ padding: '7px 14px', border: '1.5px solid #E5E2D9', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 13, flexShrink: 0, transition: 'border-color 0.12s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='#1B3A5C'}
            onMouseLeave={e => e.currentTarget.style.borderColor='#E5E2D9'}>
            Re-record
          </button>
          <button onClick={handleSubmit} disabled={loading}
            style={{ padding: '9px 18px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: loading ? 0.6 : 1, flexShrink: 0, transition: 'background 0.12s' }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background='#142d47'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background='#1B3A5C'; }}>
            {loading ? 'Processing…' : 'Submit answer'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Chat message bubbles ───────────────────────────────────────────────────────
function AIMessage({ children }) {
  return (
    <div style={{ display: 'flex', gap: 12, maxWidth: '88%', alignSelf: 'flex-start' }}>
      <AIAvatar />
      <div style={{
        background: '#fff', border: '1px solid #E5E2D9',
        borderRadius: '4px 18px 18px 18px',
        padding: '16px 20px', fontSize: 15, lineHeight: 1.65,
        color: '#12110F', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}>
        {children}
      </div>
    </div>
  );
}

function UserMessage({ text }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignSelf: 'flex-end', maxWidth: '78%' }}>
      <div style={{
        background: '#1B3A5C', color: '#fff',
        borderRadius: '18px 4px 18px 18px',
        padding: '13px 18px', fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap',
      }}>
        {text}
      </div>
    </div>
  );
}

// ── Feedback bubble ────────────────────────────────────────────────────────────
function FeedbackMessage({ evaluation, transcript }) {
  return (
    <AIMessage>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <ScoreBadge score={evaluation.score} />
        {evaluation.clarity_score !== undefined && (
          <span style={{ fontSize: 12, color: '#78756E' }}>
            · Clarity: <strong style={{ color: '#7C3AED' }}>{evaluation.clarity_score}/10</strong>
          </span>
        )}
      </div>
      {transcript && (
        <div style={{ background: '#F9F8F5', borderRadius: 10, padding: '9px 14px', marginBottom: 12, fontSize: 13, color: '#78756E' }}>
          <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>Transcript · </span>
          {transcript}
        </div>
      )}
      <p style={{ margin: '0 0 12px', fontSize: 14, color: '#44403C', lineHeight: 1.72 }}>{evaluation.feedback}</p>
      {evaluation.improvement_tips && (
        <div style={{ background: '#DBEAFE', borderRadius: 10, padding: '11px 14px', fontSize: 13, color: '#1D4ED8', lineHeight: 1.65 }}>
          <span style={{ fontWeight: 700 }}>Tip: </span>
          {evaluation.improvement_tips}
        </div>
      )}
      {evaluation.clarity_notes && (
        <div style={{ background: '#EDE9FE', borderRadius: 10, padding: '11px 14px', fontSize: 13, color: '#7C3AED', lineHeight: 1.65, marginTop: 10 }}>
          <span style={{ fontWeight: 700 }}>Communication: </span>
          {evaluation.clarity_notes}
        </div>
      )}
    </AIMessage>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function QuizSessionPage() {
  const { subChapterId } = useParams();
  const navigate = useNavigate();

  const [subChapter, setSubChapter]   = useState(null);
  const [questions,  setQuestions]    = useState([]);
  const [progressId, setProgressId]   = useState(null);
  const [answers,    setAnswers]       = useState({});
  const [activeIdx,  setActiveIdx]     = useState(0);
  const [writtenText, setWrittenText]  = useState('');
  const [submitting,  setSubmitting]   = useState(false);
  const [completing,  setCompleting]   = useState(false);
  const [loading,     setLoading]      = useState(true);
  const [sidebarOpen, setSidebarOpen]  = useState(true);
  const [bookmarks,   setBookmarks]    = useState(() => {
    try { return JSON.parse(localStorage.getItem(`ip_bm_${subChapterId}`) || '[]'); } catch { return []; }
  });
  const [confidence,  setConfidence]   = useState({});
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [explainOpen,    setExplainOpen]    = useState(false);
  const [explanation,    setExplanation]    = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [timeLeft,     setTimeLeft]    = useState(120);
  const [timerLimit,   setTimerLimit]  = useState(120);
  const timerRef2 = useRef(null);

  const bottomRef  = useRef(null);
  const msgRefs    = useRef({});
  const textareaRef = useRef(null);

  useEffect(() => {
    Promise.all([
      getSubChapterQuestions(subChapterId),
      startProgress(subChapterId),
    ]).then(([qRes, pRes]) => {
      setSubChapter(qRes.data.sub_chapter);
      setQuestions(qRes.data.questions);
      setProgressId(pRes.data.progress.id);
      const existing = {};
      pRes.data.existing_answers.forEach(a => { existing[a.question_id] = { submitted: true }; });
      setAnswers(existing);
      const firstUnanswered = qRes.data.questions.findIndex(q => !existing[q.id]?.submitted);
      setActiveIdx(firstUnanswered === -1 ? qRes.data.questions.length - 1 : firstUnanswered);
    }).catch(err => {
      if (err.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
        toast.error('Subscription required');
        navigate(`/subscribe/${err.response.data.role_slug}`);
      } else if (err.response?.data?.code === 'STUDY_REQUIRED') {
        toast('Study the material first', { icon: '📖' });
        navigate(`/study/${subChapterId}`, { replace: true });
      } else {
        toast.error('Failed to load session');
        navigate(-1);
      }
    }).finally(() => setLoading(false));
  }, [subChapterId]);

  useEffect(() => {
    if (!loading && msgRefs.current[activeIdx]) {
      msgRefs.current[activeIdx].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeIdx, loading]);

  useEffect(() => {
    const q = questions[activeIdx];
    if (q?.type === 'written' && !answers[q.id]?.submitted) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
    setWrittenText('');
    if (timerEnabled) setTimeLeft(timerLimit);
    setExplainOpen(false);
    setExplanation(null);
  }, [activeIdx]);

  useEffect(() => {
    if (!timerEnabled) { clearInterval(timerRef2.current); return; }
    const q = questions[activeIdx];
    if (!q || answers[q.id]?.submitted) return;
    clearInterval(timerRef2.current);
    timerRef2.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef2.current); toast('Time\'s up! Moving on.', { icon: '⏱' }); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef2.current);
  }, [activeIdx, timerEnabled, answers]);

  useEffect(() => {
    localStorage.setItem(`ip_bm_${subChapterId}`, JSON.stringify(bookmarks));
  }, [bookmarks, subChapterId]);

  const toggleBookmark = (qId) => {
    setBookmarks(b => b.includes(qId) ? b.filter(x => x !== qId) : [...b, qId]);
  };

  const currentQ    = questions[activeIdx];
  const allAnswered = questions.length > 0 && questions.every(q => answers[q.id]?.submitted);
  const answeredCount = Object.values(answers).filter(a => a.submitted).length;

  const afterSubmit = useCallback((qId, result) => {
    setAnswers(a => ({ ...a, [qId]: { submitted: true, ...result } }));
    const nextIdx = questions.findIndex((q, i) => i > activeIdx && !answers[q.id]?.submitted);
    if (nextIdx !== -1) {
      setTimeout(() => setActiveIdx(nextIdx), 600);
    } else {
      const anyUnanswered = questions.findIndex((q, i) => i !== activeIdx && !answers[q.id]?.submitted);
      if (anyUnanswered !== -1) setTimeout(() => setActiveIdx(anyUnanswered), 600);
    }
  }, [questions, activeIdx, answers]);

  const handleMCQ = async (selected) => {
    setSubmitting(true);
    try {
      const r = await submitMCQ({ question_id: currentQ.id, progress_id: progressId, user_answer: selected });
      afterSubmit(currentQ.id, r.data);
    } catch { toast.error('Submission failed'); }
    finally { setSubmitting(false); }
  };

  const handleWritten = async () => {
    if (!writtenText.trim()) return toast.error('Write an answer first');
    setSubmitting(true);
    try {
      const r = await submitWritten({ question_id: currentQ.id, progress_id: progressId, user_answer: writtenText });
      afterSubmit(currentQ.id, r.data);
    } catch { toast.error('Submission failed'); }
    finally { setSubmitting(false); }
  };

  const handleSpoken = async (base64, mimeType) => {
    setSubmitting(true);
    try {
      const r = await submitSpoken({ question_id: currentQ.id, progress_id: progressId, audio_base64: base64, mime_type: mimeType });
      afterSubmit(currentQ.id, r.data);
    } catch { toast.error('Evaluation failed'); }
    finally { setSubmitting(false); }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await completeProgress(progressId);
      navigate(`/results/${progressId}`);
    } catch { toast.error('Failed to complete session'); setCompleting(false); }
  };

  const handleExplain = async () => {
    if (explanation) { setExplainOpen(true); return; }
    setExplainOpen(true);
    setExplainLoading(true);
    try {
      const r = await explainConcept(currentQ.id);
      setExplanation(r.data.explanation);
    } catch {
      toast.error('Could not load explanation — try again');
      setExplainOpen(false);
    } finally {
      setExplainLoading(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F2F1ED' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  const progressPct = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;
  const currentAnswered = answers[currentQ?.id]?.submitted;
  const timerPct = (timeLeft / timerLimit) * 100;
  const timerColor = timerPct > 50 ? '#1B3A5C' : timerPct > 20 ? '#D97706' : '#C1272D';

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", background: '#F2F1ED' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
        @keyframes spin         { to { transform: rotate(360deg); } }
        @keyframes recPulse     { 0%,100%{opacity:1} 50%{opacity:.55} }
        @keyframes fadeIn       { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes slideInRight { from{transform:translateX(100%)} to{transform:none} }
        @keyframes explainIn    { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:none} }
        .msg-in { animation: fadeIn 0.28s ease; }
        .q-item:hover { background: rgba(0,0,0,0.035) !important; }
        .chat-textarea:focus { border-color: #1B3A5C !important; outline: none; box-shadow: 0 0 0 3px rgba(27,58,92,0.1); }
        @media (max-width: 640px) { .quiz-sidebar { display: none !important; } }
      `}</style>

      {/* ── Left sidebar ──────────────────────────────────────────────────── */}
      <aside className="quiz-sidebar" style={{
        width: sidebarOpen ? 270 : 60, background: '#FFFFFF',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        transition: 'width 0.2s ease', overflow: 'hidden',
        borderRight: '1px solid #E2E8F0',
      }}>
        {/* Header */}
        <div style={{
          padding: sidebarOpen ? '14px 16px 12px' : '14px 0 12px',
          borderBottom: '1px solid #E2E8F0', flexShrink: 0,
          display: 'flex', alignItems: 'center',
          justifyContent: sidebarOpen ? 'space-between' : 'center', gap: 8,
        }}>
          {sidebarOpen && (
            <div style={{ minWidth: 0 }}>
              <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: '2px 0', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 7 }}>
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                Back
              </button>
              <p style={{ margin: 0, color: '#0F172A', fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subChapter?.title}</p>
              <p style={{ margin: '2px 0 0', color: '#475569', fontSize: 11, whiteSpace: 'nowrap' }}>
                {subChapter?.role_title} · Ch {subChapter?.chapter_number}
              </p>
            </div>
          )}
          <button onClick={() => setSidebarOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', flexShrink: 0, transition: 'color 0.12s' }}
            onMouseEnter={e => e.currentTarget.style.color='#1B3A5C'}
            onMouseLeave={e => e.currentTarget.style.color='#475569'}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              {sidebarOpen
                ? <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 9l-3 3 3 3"/></>
                : <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M9 9l3 3-3 3"/></>
              }
            </svg>
          </button>
        </div>

        {/* Progress bar in sidebar */}
        {sidebarOpen && (
          <div style={{ padding: '10px 16px 8px', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Progress</span>
              <span style={{ fontSize: 11, color: '#1B3A5C', fontWeight: 700 }}>{answeredCount}/{questions.length}</span>
            </div>
            <div style={{ height: 4, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: '#C1272D', borderRadius: 2, transition: 'width 0.4s' }} />
            </div>
          </div>
        )}

        {/* Question list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', scrollbarWidth: 'none' }}>
          {questions.map((q, i) => {
            const done   = !!answers[q.id]?.submitted;
            const active = i === activeIdx;
            return (
              <button key={q.id} className="q-item" onClick={() => setActiveIdx(i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  gap: sidebarOpen ? 10 : 0,
                  justifyContent: sidebarOpen ? 'flex-start' : 'center',
                  padding: sidebarOpen ? '9px 16px' : '9px 0',
                  background: active ? '#EFF6FF' : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.12s',
                }}>
                {/* Status dot — navy=answered, red=current, gray=unanswered */}
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: done ? '#1B3A5C' : active ? '#C1272D' : '#F1F5F9',
                  border: active && !done ? '1.5px solid #CBD5E1' : 'none',
                  fontSize: 10, fontWeight: 700, color: (done || active) ? '#fff' : '#64748B',
                }}>
                  {done ? (
                    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : i + 1}
                </div>
                {sidebarOpen && (
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 12, color: active ? '#1B3A5C' : done ? '#334155' : '#64748B', fontWeight: active ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 155 }}>
                      {q.content.slice(0, 52)}{q.content.length > 52 ? '…' : ''}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                      <span style={{ fontSize: 10, color: TYPE_COLOR[q.type], background: TYPE_BG[q.type], padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                        {TYPE_LABEL[q.type]}
                      </span>
                      {bookmarks.includes(q.id) && (
                        <svg width={10} height={10} viewBox="0 0 24 24" fill="#1B3A5C" stroke="#1B3A5C" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
                        </svg>
                      )}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Finish button */}
        {sidebarOpen && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #E2E8F0', flexShrink: 0 }}>
            <button onClick={allAnswered ? handleComplete : () => {
              const first = questions.findIndex(q => !answers[q.id]?.submitted);
              if (first !== -1) setActiveIdx(first);
              toast('Answer all questions first', { icon: '👆' });
            }}
              disabled={completing}
              style={{
                width: '100%', padding: '10px', borderRadius: 10, border: 'none',
                background: allAnswered ? '#C1272D' : '#F1F5F9',
                color: allAnswered ? '#fff' : '#475569',
                fontWeight: 700, fontSize: 13, cursor: completing ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s', opacity: completing ? 0.7 : 1,
                fontFamily: 'inherit',
              }}
              onMouseEnter={e => { if (allAnswered && !completing) e.currentTarget.style.background='#a01f24'; }}
              onMouseLeave={e => { if (allAnswered) e.currentTarget.style.background='#C1272D'; }}>
              {completing ? 'Finishing…' : allAnswered ? 'View results →' : `${answeredCount}/${questions.length} answered`}
            </button>
          </div>
        )}
      </aside>

      {/* ── AI Explain panel (left chat) ────────────────────────────────────── */}
      {explainOpen && (
        <div style={{
          width: 360, minWidth: 360, background: '#fff', flexShrink: 0,
          borderRight: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column',
          animation: 'explainIn 0.22s ease',
        }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: '#F8FAFC' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <AIAvatar />
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#0F172A' }}>AI Explanation</p>
                <p style={{ margin: 0, fontSize: 11, color: '#64748B' }}>Concept, not the answer</p>
              </div>
            </div>
            <button onClick={() => setExplainOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 6, borderRadius: 6, display: 'flex', transition: 'color 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.color='#0F172A'}
              onMouseLeave={e => e.currentTarget.style.color='#64748B'}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Chat body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Question context chip */}
            {currentQ && (
              <div style={{ background: '#F1F5F9', borderRadius: 10, padding: '10px 14px' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#1B3A5C', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>Question</span>
                <p style={{ margin: 0, fontSize: 12, color: '#334155', lineHeight: 1.6 }}>{currentQ.content}</p>
              </div>
            )}

            {/* AI response bubble */}
            {explainLoading ? (
              <div style={{ display: 'flex', gap: 10, alignSelf: 'flex-start', maxWidth: '100%' }}>
                <AIAvatar />
                <div style={{ background: '#fff', border: '1px solid #E5E2D9', borderRadius: '4px 18px 18px 18px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <div style={{ width: 16, height: 16, border: '2px solid #E2E8F0', borderTopColor: '#1B3A5C', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#64748B' }}>Thinking…</span>
                </div>
              </div>
            ) : explanation ? (
              <div style={{ display: 'flex', gap: 10, alignSelf: 'flex-start', maxWidth: '100%' }}>
                <div style={{ flexShrink: 0 }}><AIAvatar /></div>
                <div style={{ background: '#fff', border: '1px solid #E5E2D9', borderRadius: '4px 18px 18px 18px', padding: '14px 16px', fontSize: 13, lineHeight: 1.75, color: '#12110F', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  {explanation.split('\n\n').map((para, i) =>
                    para.trim() ? <p key={i} style={{ margin: '0 0 10px' }}>{para.trim()}</p> : null
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', flexShrink: 0 }}>
            <p style={{ margin: 0, fontSize: 11, color: '#94A3B8', textAlign: 'center', lineHeight: 1.5 }}>
              Close and answer from your own understanding
            </p>
          </div>
        </div>
      )}

      {/* ── Main content — single question card ─────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Top bar */}
        <div style={{ height: 56, background: '#fff', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', flexShrink: 0, gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subChapter?.title}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>
              {subChapter?.role_title} · Chapter {subChapter?.chapter_number}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {/* Timer toggle */}
            <button onClick={() => { setTimerEnabled(t => !t); setTimeLeft(timerLimit); }}
              title={timerEnabled ? 'Disable timer' : 'Enable timer'}
              style={{
                background: timerEnabled ? '#1B3A5C' : '#F8FAFC',
                border: timerEnabled ? 'none' : '1px solid #E2E8F0',
                borderRadius: 8, padding: '5px 11px', cursor: 'pointer',
                fontSize: 12, fontWeight: 700,
                color: timerEnabled ? (timeLeft < 30 ? '#FCA5A5' : '#fff') : '#64748B',
                display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s', fontFamily: 'inherit',
              }}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {timerEnabled ? `${Math.floor(timeLeft/60)}:${String(timeLeft%60).padStart(2,'0')}` : 'Timer'}
            </button>
            {/* Progress */}
            <span style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>{answeredCount}/{questions.length}</span>
            <div style={{ width: 80, height: 5, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: '#1B3A5C', borderRadius: 3, transition: 'width 0.4s' }} />
            </div>
          </div>
        </div>

        {/* Timer strip */}
        {timerEnabled && !currentAnswered && (
          <div style={{ height: 3, background: '#E2E8F0', flexShrink: 0 }}>
            <div style={{ height: '100%', width: `${timerPct}%`, background: timerColor, transition: 'width 1s linear, background 0.5s' }} />
          </div>
        )}

        {/* ── Single question card ─────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 20px', background: '#F8FAFC' }}>
          <div style={{ maxWidth: 680, margin: '0 auto' }}>

            {currentQ && (
              <div key={currentQ.id} className="msg-in" style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', borderTop: `3px solid ${TYPE_COLOR[currentQ.type]}` }}>

                {/* Card header */}
                <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: '#EFF6FF', color: '#1B3A5C' }}>
                    Q{activeIdx + 1} of {questions.length}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: TYPE_BG[currentQ.type], color: TYPE_COLOR[currentQ.type] }}>
                    {TYPE_LABEL[currentQ.type]}
                  </span>
                  {currentQ.type === 'written' && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: TYPE_COLOR.written, background: TYPE_BG.written, padding: '2px 8px', borderRadius: 5, marginLeft: 2 }}>✍ Type your answer below</span>
                  )}
                  {currentQ.type === 'spoken' && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: TYPE_COLOR.spoken, background: TYPE_BG.spoken, padding: '2px 8px', borderRadius: 5, marginLeft: 2 }}>🎙 Speak your answer below</span>
                  )}
                  {/* Bookmark */}
                  <button onClick={() => toggleBookmark(currentQ.id)}
                    aria-label={bookmarks.includes(currentQ.id) ? 'Remove bookmark' : 'Bookmark'}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: bookmarks.includes(currentQ.id) ? '#1B3A5C' : '#94A3B8', transition: 'color 0.15s', display: 'flex' }}
                    onMouseEnter={e => e.currentTarget.style.color='#1B3A5C'}
                    onMouseLeave={e => { if (!bookmarks.includes(currentQ.id)) e.currentTarget.style.color='#94A3B8'; }}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill={bookmarks.includes(currentQ.id) ? '#1B3A5C' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
                    </svg>
                  </button>
                </div>

                {/* Question text */}
                <div style={{ padding: '14px 20px 16px' }}>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 600, lineHeight: 1.65, color: '#0F172A' }}>
                    {currentQ.content}
                  </p>
                </div>

                {/* ── Explain concept strip ── */}
                <div style={{ padding: '0 20px 16px' }}>
                  <button onClick={handleExplain}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '13px 16px', borderRadius: 12, cursor: 'pointer',
                      border: `1.5px solid ${explainOpen ? '#C1272D' : '#FECACA'}`,
                      background: explainOpen ? '#FEF2F2' : '#FFF5F5',
                      transition: 'all 0.15s', fontFamily: 'inherit', textAlign: 'left',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#C1272D'; e.currentTarget.style.background='#FEF2F2'; }}
                    onMouseLeave={e => { if (!explainOpen) { e.currentTarget.style.borderColor='#FECACA'; e.currentTarget.style.background='#FFF5F5'; } }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: explainOpen ? '#FECACA' : '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s', fontSize: 18 }}>
                      💡
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#991B1B' }}>
                        {explainOpen ? 'AI explanation is open' : 'Stuck on this concept?'}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: '#C1272D', marginTop: 2 }}>
                        {explainOpen ? 'See the panel on the left ←' : 'Get an instant AI explanation — won\'t reveal the answer'}
                      </p>
                    </div>
                    {!explainOpen ? (
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#C1272D', padding: '4px 12px', borderRadius: 20, flexShrink: 0 }}>
                        Explain →
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#991B1B', flexShrink: 0 }}>← open</span>
                    )}
                  </button>
                </div>

                {/* Answer input — inside the card */}
                {!currentAnswered && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '20px' }}>
                    {currentQ.type === 'mcq' && (
                      <MCQOptions question={currentQ} onSubmit={handleMCQ} submitted={false} result={null} />
                    )}
                    {currentQ.type === 'written' && (
                      <div>
                        <textarea ref={textareaRef} className="chat-textarea"
                          value={writtenText} onChange={e => setWrittenText(e.target.value)}
                          disabled={submitting}
                          placeholder="Type your answer here…"
                          rows={5}
                          maxLength={5000}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleWritten(); }}
                          style={{
                            width: '100%', padding: '14px 16px', border: '1.5px solid #E2E8F0', borderRadius: 12,
                            fontSize: 15, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.7,
                            background: submitting ? '#F8FAFC' : '#fff', color: '#0F172A',
                            transition: 'border-color 0.15s, box-shadow 0.15s', boxSizing: 'border-box', display: 'block',
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                          <span style={{ fontSize: 11, color: writtenText.length > 4500 ? '#C1272D' : '#475569' }}>
                            {writtenText.length}/5,000 · ⌘↵ to submit
                          </span>
                          <button onClick={handleWritten} disabled={!writtenText.trim() || submitting}
                            style={{
                              padding: '10px 24px', borderRadius: 10, border: 'none',
                              background: writtenText.trim() && !submitting ? '#1B3A5C' : '#E2E8F0',
                              color: writtenText.trim() && !submitting ? '#fff' : '#475569',
                              fontSize: 14, fontWeight: 700, cursor: writtenText.trim() && !submitting ? 'pointer' : 'not-allowed',
                              fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 8,
                            }}
                            onMouseEnter={e => { if (writtenText.trim() && !submitting) e.currentTarget.style.background='#142d47'; }}
                            onMouseLeave={e => { if (writtenText.trim() && !submitting) e.currentTarget.style.background='#1B3A5C'; }}>
                            {submitting
                              ? <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Evaluating…</>
                              : 'Submit written answer'}
                          </button>
                        </div>
                      </div>
                    )}
                    {currentQ.type === 'spoken' && (
                      <div>
                        <div style={{ padding: '16px', background: '#FEF3C7', borderRadius: 10, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                          <span style={{ fontSize: 13, color: '#92400E', fontWeight: 500 }}>
                            Click the red button to start recording. Speak your answer clearly, then stop and submit.
                          </span>
                        </div>
                        <SpokenRecorder onReady={handleSpoken} loading={submitting} />
                      </div>
                    )}
                  </div>
                )}

                {/* MCQ result feedback */}
                {currentAnswered && currentQ.type === 'mcq' && answers[currentQ.id] && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '16px 20px' }}>
                    <MCQOptions question={currentQ} onSubmit={handleMCQ} submitted={true} result={answers[currentQ.id]} />
                  </div>
                )}

                {/* Written/Spoken feedback */}
                {currentAnswered && currentQ.type !== 'mcq' && answers[currentQ.id]?.evaluation && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '20px' }}>
                    {answers[currentQ.id].user_answer && (
                      <div style={{ background: '#F0F6FF', borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 14, color: '#334155', lineHeight: 1.65 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1B3A5C', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Your answer</span>
                        {answers[currentQ.id].transcript || answers[currentQ.id].user_answer}
                      </div>
                    )}
                    <FeedbackMessage evaluation={answers[currentQ.id].evaluation} transcript={answers[currentQ.id].transcript} />
                  </div>
                )}

                {/* Confidence rating */}
                {currentAnswered && !confidence[currentQ.id] && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>How confident were you?</span>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setConfidence(c => ({ ...c, [currentQ.id]: n }))}
                        aria-label={`Confidence level ${n}`}
                        style={{ width: 32, height: 32, borderRadius: '50%', border: '1.5px solid #E2E8F0', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#334155', transition: 'all 0.12s', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}
                        onMouseEnter={e => { e.currentTarget.style.background='#1B3A5C'; e.currentTarget.style.color='#fff'; e.currentTarget.style.borderColor='#1B3A5C'; }}
                        onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.color='#334155'; e.currentTarget.style.borderColor='#E2E8F0'; }}
                      >{n}</button>
                    ))}
                  </div>
                )}
                {currentAnswered && confidence[currentQ.id] && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '12px 20px' }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#1B3A5C', fontWeight: 500 }}>Confidence: {'★'.repeat(confidence[currentQ.id])}{'☆'.repeat(5 - confidence[currentQ.id])}</p>
                  </div>
                )}

              </div>
            )}

            {/* ── Navigation ──────────────────────────────────────────────── */}
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <button onClick={() => setActiveIdx(i => Math.max(0, i - 1))} disabled={activeIdx === 0}
                style={{ padding: '10px 18px', border: '1.5px solid #E2E8F0', borderRadius: 10, background: '#fff', cursor: activeIdx === 0 ? 'not-allowed' : 'pointer', opacity: activeIdx === 0 ? 0.4 : 1, fontSize: 13, fontFamily: 'inherit', fontWeight: 600, color: '#334155', transition: 'all 0.15s' }}
                onMouseEnter={e => { if (activeIdx !== 0) { e.currentTarget.style.borderColor='#1B3A5C'; e.currentTarget.style.color='#1B3A5C'; } }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='#E2E8F0'; e.currentTarget.style.color='#334155'; }}>
                ← Previous
              </button>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {allAnswered ? (
                  <button onClick={handleComplete} disabled={completing}
                    style={{ padding: '11px 28px', background: '#C1272D', color: '#fff', border: 'none', borderRadius: 10, cursor: completing ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, opacity: completing ? 0.7 : 1, fontFamily: 'inherit', transition: 'background 0.15s', boxShadow: '0 4px 16px rgba(193,39,45,0.25)' }}
                    onMouseEnter={e => { if (!completing) e.currentTarget.style.background='#a01f24'; }}
                    onMouseLeave={e => { if (!completing) e.currentTarget.style.background='#C1272D'; }}>
                    {completing ? 'Finishing…' : 'View my results →'}
                  </button>
                ) : currentAnswered && activeIdx < questions.length - 1 ? (
                  <button onClick={() => {
                    const next = questions.findIndex((q, i) => i > activeIdx && !answers[q.id]?.submitted);
                    setActiveIdx(next !== -1 ? next : activeIdx + 1);
                  }}
                    style={{ padding: '11px 28px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                    onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                    Next question →
                  </button>
                ) : !currentAnswered ? (
                  <span style={{ fontSize: 12, color: '#fff', fontWeight: 700, padding: '8px 18px', background: '#C1272D', borderRadius: 20, boxShadow: '0 2px 8px rgba(193,39,45,0.3)' }}>
                    ↑ Select an answer to continue
                  </span>
                ) : null}
              </div>
            </div>

            {/* ── Question dots ────────────────────────────────────────────── */}
            <div style={{ marginTop: 20, display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'center' }}>
              {questions.map((q, i) => {
                const done   = !!answers[q.id]?.submitted;
                const active = i === activeIdx;
                return (
                  <button key={q.id} onClick={() => setActiveIdx(i)} title={`Q${i+1}: ${TYPE_LABEL[q.type]}`}
                    style={{
                      width: active ? 28 : 10, height: 10, borderRadius: 5, border: 'none', cursor: 'pointer', padding: 0,
                      background: active ? '#1B3A5C' : done ? '#059669' : '#E2E8F0',
                      transition: 'all 0.2s ease', flexShrink: 0,
                    }}
                  />
                );
              })}
            </div>

            <div ref={bottomRef} style={{ height: 24 }} />
          </div>
        </div>

      </div>

    </div>
  );
}
